/*
 * Trends.tsx — S57: Trend Analysis & Monitoring
 * API: GET /api/v1/monitoring/trends, GET /api/v1/monitoring/trends/alerts
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch, titleCase } from '../constants';

interface TrendAlert {
  id: number;
  alert_type: string;
  severity: string;
  description: string;
  message?: string;
  status: string;
  entity_id: number | null;
  exception_type: string | null;
  detected_at: string | null;
  acknowledged_by: string | null;
}

interface TypeTrend {
  type: string;
  current: number;
  previous: number;
  change_pct: number;
}

interface TrendDash {
  current_period: string;
  previous_period: string;
  exception_volume: { current: number; previous: number; change_pct: number };
  by_type: TypeTrend[];
  active_alerts: TrendAlert[];
  active_alert_count: number;
}

export default function Trends() {
  const [dashboard, setDashboard] = useState<TrendDash | null>(null);
  const [alerts, setAlerts] = useState<TrendAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('ALL');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/monitoring/trends`, null),
      safeFetch(`${API_V1}/monitoring/trends/alerts?limit=100`, { alerts: [] }),
    ])
      .then(([dash, alertRes]) => {
        setDashboard(dash);
        setAlerts(alertRes?.alerts ?? []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading trend analysis...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const sevColor = (s: string) => {
    if (s === 'HIGH' || s === 'CRITICAL') return 'var(--red)';
    if (s === 'MEDIUM') return 'var(--amber)';
    return 'var(--green)';
  };

  const statusBadge = (s: string) => {
    const c = s === 'ACTIVE' || s === 'NEW' ? 'var(--red)' : s === 'ACKNOWLEDGED' ? 'var(--blue)' : 'var(--text-tertiary)';
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: `${c}20`, color: c,
      }}>
        {s}
      </span>
    );
  };

  const changeBadge = (pct: number) => {
    const color = pct > 10 ? 'var(--red)' : pct < -10 ? 'var(--green)' : 'var(--amber)';
    const arrow = pct > 0 ? '\u2191' : pct < 0 ? '\u2193' : '\u2192';
    return (
      <span className="mono" style={{ color, fontWeight: 600, fontSize: 12 }}>
        {arrow} {Math.abs(pct).toFixed(1)}%
      </span>
    );
  };

  const vol = dashboard?.exception_volume;
  const byType = dashboard?.by_type ?? [];

  // Derive severity counts from alerts
  const bySeverity: Record<string, number> = {};
  for (const a of alerts) {
    bySeverity[a.severity] = (bySeverity[a.severity] ?? 0) + 1;
  }

  const severities = ['ALL', ...Array.from(new Set(alerts.map(a => a.severity)))];
  const filtered = filterSeverity === 'ALL' ? alerts : alerts.filter(a => a.severity === filterSeverity);

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>Trend Analysis</h2>
        <button onClick={load} style={{ padding: '6px 16px', fontSize: 12 }}>Refresh</button>
      </div>

      {/* Volume overview */}
      {vol && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="stitle" style={{ marginBottom: 4 }}>Current ({dashboard?.current_period})</div>
            <div className="mono" style={{ fontSize: 24, fontWeight: 700 }}>{(vol.current ?? 0).toLocaleString()}</div>
          </div>
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="stitle" style={{ marginBottom: 4 }}>Previous ({dashboard?.previous_period})</div>
            <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-secondary)' }}>{(vol.previous ?? 0).toLocaleString()}</div>
          </div>
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="stitle" style={{ marginBottom: 4 }}>Change</div>
            <div style={{ fontSize: 24 }}>{changeBadge(vol.change_pct ?? 0)}</div>
          </div>
        </div>
      )}

      {/* By type trends */}
      {byType.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 className="stitle" style={{ marginBottom: 12 }}>By Exception Type</h3>
          {byType.slice(0, 10).map(t => (
            <div key={t.type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 13, flex: 1 }}>{titleCase(t.type)}</span>
              <span className="mono" style={{ width: 60, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{t.current}</span>
              <span style={{ width: 80, textAlign: 'right' }}>{changeBadge(t.change_pct ?? 0)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Alert severity breakdown */}
      {Object.keys(bySeverity).length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 className="stitle" style={{ marginBottom: 12 }}>Alerts by Severity</h3>
          <div style={{ display: 'flex', gap: 16 }}>
            {Object.entries(bySeverity).map(([sev, count]) => (
              <div key={sev} style={{ flex: 1, textAlign: 'center' }}>
                <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: sevColor(sev) }}>{count}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{sev}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert filter + list */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {severities.map(s => (
          <button key={s} onClick={() => setFilterSeverity(s)}
            style={{
              padding: '4px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              background: filterSeverity === s ? 'var(--accent)' : 'var(--bg-surface)',
              color: filterSeverity === s ? '#fff' : 'var(--text-secondary)',
              border: 'none',
            }}>
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>No trend alerts detected.</p>
        </div>
      ) : (
        filtered.map(a => (
          <div key={a.id} className="card" style={{ padding: 16, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: `${sevColor(a.severity)}20`, color: sevColor(a.severity),
                }}>
                  {a.severity}
                </span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{titleCase(a.alert_type)}</span>
                {statusBadge(a.status)}
              </div>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>#{a.id}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>{a.description || a.message || ''}</div>
            <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-tertiary)' }}>
              {a.exception_type && <span>Type: {titleCase(a.exception_type)}</span>}
              {a.entity_id && <span>Entity: {a.entity_id}</span>}
              {a.detected_at && <span>Detected: {new Date(a.detected_at).toLocaleDateString()}</span>}
              {a.acknowledged_by && <span>Ack: {a.acknowledged_by}</span>}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
